/**
 * Keeps the price beside the product title in step with everything that can
 * change it: the Shopify variant, Recharge's purchase option / delivery
 * frequency, and the quantity selector.
 *
 * It also owns that quantity selector on products whose Recharge tiers ship
 * differing unit counts, since there the plan dictates the count rather than the
 * shopper. Products whose tiers all ship a single unit — every tier before the
 * upsell tiers — are left alone entirely.
 *
 * Recharge's widget publishes no events and renders into a shadow root, but it
 * does keep a light-DOM `selling_plan` field in the add-to-cart form current,
 * rewriting it with setAttribute on every change — so that field is the signal
 * for the plan half, and the theme's own `variant:change` event for the other.
 *
 * Prices come from the server-rendered allocation map, so the title shows what
 * Shopify will actually charge. A Recharge tier with no Shopify selling plan
 * behind it yet won't be in that map; those fall back to the widget's `prices`
 * getter so an in-progress tier still displays.
 */
(function () {
  var priceEl = document.querySelector('.product-single__prices');
  var dataEl = document.querySelector('[id^="PdpPriceData-"]');
  var mainForm = document.getElementById('add-to-cart');
  if (!priceEl || !dataEl || !mainForm) return;

  var variantPrices;
  try {
    variantPrices = JSON.parse(dataEl.textContent).variants || {};
  } catch (err) {
    console.error(err);
    return;
  }

  var quantityEl = document.querySelector('.js-quantity-selector');
  var quantityWrap = quantityEl?.closest('.quantity-outer');
  // True only where some tier ships more than one unit. Products whose tiers all
  // ship one keep a selector this module never touches.
  var multiUnitProduct = Object.keys(variantPrices).some(function (id) {
    var plans = variantPrices[id].plans || {};
    return Object.keys(plans).some(function (planId) {
      return plans[planId].units > 1;
    });
  });

  var watchedField = null;
  var lastRendered = null;
  var lastSyncedSelection = null;
  var frame = null;

  // Matches the site-wide euro fix in layout/theme.liquid, which only runs on
  // DOMContentLoaded and so never sees the prices written here.
  function normalizeEuro(price) {
    return price.charAt(0) === '€'
      ? '€' + price.slice(1).replace(',', '.')
      : price;
  }

  function selectedVariantId() {
    return mainForm.querySelector('[name="id"]')?.value || null;
  }

  // '' means one-time; null means Recharge hasn't hydrated its field yet, which
  // is different from a deliberate one-time selection.
  function selectedSellingPlanId() {
    var field =
      mainForm.querySelector('input[name="selling_plan"]:checked') ||
      mainForm.querySelector('[name="selling_plan"]');
    return field ? field.value : null;
  }

  // Resolved price for any variant/plan pair, empty plan meaning one-time.
  // Null when nothing in Shopify backs that pair. Also used by the sticky ATC.
  function priceFor(variantId, sellingPlanId) {
    var variant = variantPrices[variantId];
    if (!variant) return null;
    if (!sellingPlanId) return variant;
    return variant.plans[sellingPlanId] || null;
  }

  function fromAllocations() {
    var planId = selectedSellingPlanId();
    if (planId === null) return null;
    return priceFor(selectedVariantId(), planId);
  }

  function fromWidget() {
    var prices = document.querySelector(
      'recharge-subscription-widget'
    )?.prices;
    if (!prices) return null;
    return {
      price: selectedSellingPlanId() ? prices.discounted : prices.unit,
    };
  }

  // The quantity selector holds the true unit count: the shopper's own on an
  // ordinary line, the plan's where one dictates it. Everything downstream — the
  // headline, its strikethrough, the sticky ATC — multiplies from that one number.
  function currentQuantity() {
    var value = parseInt(quantityEl?.value, 10);
    return value > 0 ? value : 1;
  }

  function money(cents) {
    return normalizeEuro(Shopify.formatMoney(cents, ''));
  }

  function render() {
    var resolved = fromAllocations() || fromWidget();
    if (!resolved || !resolved.price) return;

    var units = currentQuantity();
    var scale = units > 1 && resolved.priceCents;
    var headlinePrice = scale
      ? money(resolved.priceCents * units)
      : normalizeEuro(resolved.price);
    var comparePrice =
      scale && resolved.compareAtCents
        ? money(resolved.compareAtCents * units)
        : !scale && resolved.compareAtPrice
          ? normalizeEuro(resolved.compareAtPrice)
          : null;

    var html = '<span>' + headlinePrice + '</span>';
    if (comparePrice) {
      html += ' <s>' + comparePrice + '</s>';
    }
    // Gated on the scaled headline, not on the count alone: the widget fallback
    // carries no cents to multiply, so its headline stays the unit price and a
    // qualifier there would contradict the figure above it.
    if (scale) {
      html +=
        '<span class="price-each">' +
        units +
        ' units · ' +
        normalizeEuro(resolved.price) +
        ' per unit</span>';
    }
    if (html === lastRendered) return;
    lastRendered = html;
    priceEl.innerHTML = html;
  }

  function setQuantityLocked(locked) {
    quantityWrap?.classList.toggle('quantity-outer--locked', locked);
    if (quantityEl) quantityEl.readOnly = locked;
    quantityWrap
      ?.querySelectorAll('.js-plus-minus-qty')
      .forEach(function (step) {
        if (locked) {
          step.setAttribute('aria-disabled', 'true');
        } else {
          step.removeAttribute('aria-disabled');
        }
      });
  }

  // Some Recharge builds drop the light-DOM selling_plan field for one-time
  // rather than emptying it, which that field alone can't tell apart from "not
  // hydrated yet". The widget's checked purchase option settles it, the same way
  // the sticky ATC's readMainSelection does.
  function oneTimeSelectedInWidget() {
    var option = document
      .querySelector('recharge-subscription-widget')
      ?.shadowRoot?.querySelector('.rc-purchase-option input:checked')
      ?.closest('.rc-purchase-option');
    if (!option) return false;
    var text = (option.textContent || '').toLowerCase();
    return text.includes('one-time') || text.includes('one time');
  }

  // Drives the selector from the selected plan, and only on a multi-unit
  // product. One-time returns it to a single unit and hands it back, so a count
  // carried over from a tier can't quietly ride along on a one-time purchase.
  //
  // The count is written on a change of selection only. Rewriting it on every
  // render would undo the shopper's own edits the moment they made them, since
  // each edit fires the change event that schedules the next render.
  function syncQuantityToPlan() {
    if (!quantityEl || !multiUnitProduct) return;
    var planId = selectedSellingPlanId();
    if (planId === null && !oneTimeSelectedInWidget()) return;
    var plan = planId ? priceFor(selectedVariantId(), planId) : null;
    // Keyed off the resolved plan, not the raw id: a tier Recharge offers but
    // Shopify hasn't synced has no known unit count, so locking to a guessed 1
    // would be worse than leaving the selector alone.
    setQuantityLocked(Boolean(plan));

    // Variant-scoped only while a plan is driving the count, since unit counts
    // can differ per variant. Everything else — one-time, or a tier Shopify
    // hasn't synced — keys to a constant, so the count stays the shopper's and
    // a variant switch doesn't discard what they typed.
    var selection = plan ? selectedVariantId() + '|' + planId : 'no-plan';
    if (selection === lastSyncedSelection) return;
    lastSyncedSelection = selection;

    var target = plan?.units > 0 ? plan.units : 1;
    if (String(quantityEl.value) === String(target)) return;
    quantityEl.value = target;
    quantityEl.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function update() {
    syncQuantityToPlan();
    render();
  }

  function schedule() {
    if (frame) return;
    frame = requestAnimationFrame(function () {
      frame = null;
      update();
    });
  }

  // Recharge injects its selling_plan field after the form renders, and can
  // replace it outright, so re-bind whenever the form's contents shift.
  function watchSellingPlanField() {
    var field = mainForm.querySelector('[name="selling_plan"]');
    if (!field || field === watchedField) return;
    watchedField = field;
    new MutationObserver(schedule).observe(field, {
      attributes: true,
      attributeFilter: ['value'],
    });
  }

  new MutationObserver(function () {
    watchSellingPlanField();
    schedule();
  }).observe(mainForm, { childList: true, subtree: true });

  document.addEventListener('variant:change', schedule);
  document.addEventListener('change', function (e) {
    if (
      e.target?.matches?.(
        '[name="selling_plan"], #product-select, .js-quantity-selector'
      )
    ) {
      schedule();
    }
  });

  window.iwPdpPriceSync = { update: schedule, priceFor: priceFor };

  watchSellingPlanField();
  schedule();
})();
